import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Switch } from '@swyftx/aviary/atoms/Switch';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { Game, LightMode, Settings, TrendLineUp } from '@swyftx/aviary/icons/outlined';
import { Menu, MenuItem } from '@swyftx/aviary/molecules/Menu';

import { DarkModeEnum } from '@shared/enums';
import { UIStore } from '@shared/store';

import { capitalize } from '@utils/formatting';

import { observer } from 'mobx-react-lite';
import { useSwyftxMargin } from 'src/lib/margin/hooks';
import { useToggleTheme } from 'src/lib/utils/hooks';
import { useDemoMode } from 'src/lib/utils/hooks/useDemoMode';

const AppHeaderSettings: React.FC = observer(() => {
  const { isDemo, setDemoMode } = useDemoMode();
  const { setShowMarketTicker, showMarketTicker } = UIStore.useUIStore;
  const { t } = useTranslation('common');
  const { isMarginRoute } = useSwyftxMargin();
  const { darkMode, setDarkMode } = useToggleTheme();

  return (
    <Menu
      align='end'
      triggerContent={
        <Button
          tooltip='User preferences'
          id='header-settings'
          data-spotlightelementid='header-settings'
          variant='outlined'
          layout='icon'
          leadingIcon={<Settings />}
        />
      }
    >
      <FlexLayout direction='column'>
        {!isMarginRoute && (
          <MenuItem
            icon={<Game width={24} height={24} />}
            text={t('navigationMenu.demo')}
            endAdornment={<Switch checked={isDemo} color='warning' />}
            action={() => setDemoMode(!isDemo)}
            data-spotlightelementid='settings-menu-demo'
            preventCloseOnSelect
          />
        )}
        <Menu
          triggerContent={
            <MenuItem
              icon={<LightMode width={24} height={24} />}
              text='Theme'
              endAdornment={
                <FlexLayout alignItems='center' spacing={16}>
                  <Body size='small' color='secondary'>
                    {capitalize(darkMode)}
                  </Body>
                </FlexLayout>
              }
              subMenu={{
                align: 'start',
                side: 'left',
                alignOffset: -8,
                sideOffset: 4,
                className: 'w-[150px]',
                items: [
                  {
                    text: 'Light',
                    action: () => setDarkMode(DarkModeEnum.LIGHT),
                    selected: darkMode === DarkModeEnum.LIGHT,
                  },
                  {
                    text: 'Dark',
                    action: () => setDarkMode(DarkModeEnum.DARK),
                    selected: darkMode === DarkModeEnum.DARK,
                  },
                  {
                    text: 'Midnight',
                    action: () => setDarkMode(DarkModeEnum.MIDNIGHT),
                    selected: darkMode === DarkModeEnum.MIDNIGHT,
                  },
                ],
              }}
            />
          }
        ></Menu>

        {!isMarginRoute && (
          <MenuItem
            icon={<TrendLineUp width={24} height={24} />}
            text={t('navigationMenu.marketTicker')}
            endAdornment={<Switch checked={showMarketTicker} />}
            action={() => setShowMarketTicker(!showMarketTicker)}
            preventCloseOnSelect
          />
        )}
      </FlexLayout>
    </Menu>
  );
});

AppHeaderSettings.displayName = 'AppHeaderSettings';

export { AppHeaderSettings };
